import React from 'react';

import { classnames } from '@/utils/classnames.ts';
import usePrettyWrap from '@/utils/usePrettyWrap.ts';

import Card from './Card.tsx';
import IconTile from './IconTile.tsx';

const GhostLine = ({ className }: { className: string }) => (
    <span className={classnames('h-2.5 rounded-full bg-surface-muted', className)} />
);

// Not a multiple of the columns, so the last row is left open as a real grid's would be.
const GHOST_CARD_TITLE_WIDTHS = [
    'w-28',
    'w-20',
    'w-32',
    'w-24',
    'w-28',
    'w-20',
    'w-24',
    'w-32',
    'w-28',
    'w-20',
    'w-24',
];

interface GhostCardProps {
    titleWidth?: string;
    className?: string;
}

// As LinkCard lays out a client: its logo, name, projects and counts. Also the error page's fallen cards.
export const GhostCard = ({ titleWidth = 'w-28', className }: GhostCardProps) => (
    <Card className={classnames('flex flex-col gap-4 p-5', className)}>
        <span className="size-13 rounded-2xl bg-surface-muted" />
        <span className="flex flex-col gap-2.5 py-1">
            <GhostLine className={classnames('h-3', titleWidth)} />
            <GhostLine className="w-40" />
        </span>
        <span className="flex h-6 items-center">
            <GhostLine className="h-2 w-24" />
        </span>
    </Card>
);

const GhostGrid = () => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {GHOST_CARD_TITLE_WIDTHS.map((width, index) => (
            <GhostCard key={index} titleWidth={width} />
        ))}
    </div>
);

// As Page.ListSection lays out its rows by day: a title over a line, and rows of what it is, its title, where it
// belongs and when.
const GhostList = () => (
    <div className="flex flex-col gap-10">
        {[
            ['w-2/5', 'w-1/3'],
            ['w-1/2', 'w-1/4', 'w-2/5', 'w-1/3'],
            ['w-1/3', 'w-1/2', 'w-1/4'],
            ['w-2/5', 'w-1/3'],
        ].map((titleWidths, group) => (
            <div key={group}>
                <div className="flex h-12 items-center border-b border-border">
                    <GhostLine className="h-3.5 w-24" />
                </div>
                <div className="divide-rows">
                    {titleWidths.map((width, row) => (
                        <div key={row} className="flex min-h-11 items-center gap-3 py-2">
                            <span className="flex w-6 shrink-0 justify-center">
                                <span className="size-4 rounded bg-surface-muted" />
                            </span>
                            <span className="flex-1">
                                <GhostLine className={classnames('block', width)} />
                            </span>
                            <span className="h-6 w-32 rounded-md bg-surface-muted max-md:hidden" />
                            <span className="flex min-w-14 justify-end">
                                <GhostLine className="w-10" />
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
);

export interface EmptyPageProps extends Omit<React.ComponentPropsWithRef<'div'>, 'title'> {
    // The shape the page takes once it's filled: cards in a grid or rows in a list.
    preview: 'grid' | 'list';
    icon: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
    actions?: React.ReactNode;
}

// In place of a page's list while there's nothing in it at all, so a new workspace doesn't open on a blank page: the
// filled page faded behind a card that says what the page is for and how to fill it. A list that's only empty because
// of its filters says so with NoResults instead.
const EmptyPage = ({ preview, icon, title, description, actions, className, ...props }: EmptyPageProps) => {
    const descriptionRef = usePrettyWrap<HTMLParagraphElement>();

    return (
        // About as tall as what's left of the screen under the page's header, so the card sits in the middle of it rather
        // than at its top, and the preview runs down to where the screen ends.
        <div {...props} className={classnames('relative min-h-[max(30rem,calc(100dvh-20rem))]', className)}>
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 overflow-hidden [mask-image:linear-gradient(to_bottom,black_30%,transparent)]"
            >
                {preview === 'grid' ? <GhostGrid /> : <GhostList />}
            </div>
            <div className="absolute inset-0 flex items-center justify-center pb-[10%]">
                <Card className="flex w-full max-w-lg flex-col items-center gap-3 px-6 py-10 text-center shadow-lg shadow-black/5 sm:px-8">
                    <IconTile size="lg">{icon}</IconTile>
                    <h2 className="mt-1 text-lg font-semibold">{title}</h2>
                    <p ref={descriptionRef} className="max-w-sm text-sm text-pretty text-text-muted">
                        {description}
                    </p>
                    {actions && <div className="mt-2 flex flex-wrap justify-center gap-2">{actions}</div>}
                </Card>
            </div>
        </div>
    );
};

export default EmptyPage;
